import type { MetricConfig, FormatType } from '../../types/viewspec';

interface MetricCardProps {
  config: MetricConfig;
}

function formatValue(value: string | number, format?: FormatType): string {
  if (typeof value === 'string') return value;

  switch (format) {
    case 'number':
      return value.toLocaleString();
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    case 'percent':
      return `${(value * 100).toFixed(1)}%`;
    default:
      return value.toLocaleString();
  }
}

function getIcon(icon?: string) {
  // Using 1px stroke per Pentatonic design guidelines
  switch (icon) {
    case 'package':
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
      );
    case 'users':
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m9 5.197v1m0-1a6 6 0 00-3-5.197"
          />
        </svg>
      );
    case 'chart':
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      );
    case 'clock':
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    case 'currency':
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    default:
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
          />
        </svg>
      );
  }
}

export default function MetricCard({ config }: MetricCardProps) {
  // Pentatonic: mint for positive, coral for negative
  const trendColor =
    config.trend?.direction === 'up'
      ? 'text-mint'
      : config.trend?.direction === 'down'
      ? 'text-coral'
      : 'text-grey-medium';

  const trendIcon =
    config.trend?.direction === 'up' ? '↑' : config.trend?.direction === 'down' ? '↓' : '→';

  return (
    <div className="bg-grey-light p-6 h-full flex flex-col justify-between">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-mono text-xs text-grey-medium uppercase tracking-wider">
            {config.label}
          </p>
          <p className="text-4xl font-extralight text-black mt-2" style={{ lineHeight: 1.1 }}>
            {formatValue(config.value, config.format)}
          </p>
        </div>
        <div className="p-3 bg-black text-mint">
          {getIcon(config.icon)}
        </div>
      </div>

      {config.trend && (
        <div className={`flex items-center gap-1 mt-4 ${trendColor}`}>
          <span className="text-lg font-light">{trendIcon}</span>
          <span className="font-mono text-sm uppercase">
            {config.trend.value > 0 ? '+' : ''}
            {config.trend.value}%
          </span>
          {config.trend.label && (
            <span className="text-sm text-grey-medium font-light ml-1">
              {config.trend.label}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
